import { Entypo, Feather, MaterialIcons, Octicons } from "@expo/vector-icons";
import isWebview from "is-ua-webview";
import {
  Box,
  Button,
  Center,
  Heading,
  Image,
  KeyboardAvoidingView,
  Popover,
  PresenceTransition,
  Row,
  Stack,
  Text,
  View as NBView,
} from "native-base";
import platform from "platform-detect";
import React from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { ScreenWidth } from "react-native-elements/dist/helpers";
import { useDispatch } from "react-redux";
import usePwa from "use-pwa";

import { Images, Scale } from "../constants";
import { getAppName } from "../helpers/generalHelpers";
import { AppImage, TroubleInstallingPWAArticleComponent } from "../helpers/supportHelpers";
import { userSlice } from "../slices/userSlice";
import styles from "./LoginScreenStyles";

// General notes
// Good libraries for PWA install:
// - https://www.npmjs.com/package/@khmyznikov/pwa-install
// - https://www.npmjs.com/package/pwa-install-handler

// Expo PWA creation:
// - https://www.npmjs.com/package/expo-pwa

// NOTE: We can use the Launch Handler API (Chrome only) to add a magic link token to obviate the need for sign in
// https://developer.chrome.com/docs/web-platform/launch-handler/

// Flow:
// Android:
// [In the Instagram browser]
// - "tap the 3 dots in the top right corner"
// - "tap 'Open in Chrome'" (Dutch: "Openen in Chrome")
// - [Detect they are not in the Instagram browser]
// - "tap the 3 dots in the top right corner"
// - "tap 'Add to home screen'" (Dutch: "Toevoegen aan startscherm") ("App installeren" on safari)

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

const appName = getAppName();

const InstallInstructionsBox = (): JSX.Element => {
  const { t } = useTranslation();
  const { userAgent } = navigator;
  const areWeInsideAWebview = isWebview(userAgent);
  const isiOS = platform.ios;

  const getInstructionsText = (): string => {
    if (areWeInsideAWebview) {
      if (isiOS) {
        return t("pwa_install_screen.box.instagram_instructions_part_1_ios");
      }
      // Android
      return t("pwa_install_screen.box.instagram_instructions_part_1_android");
    }

    // Outside webview (in normal browser)
    if (isiOS) {
      if (platform.chrome) {
        return t("pwa_install_screen.box.install_instructions_part_1_ios_chrome");
      }

      // Safari
      return t("pwa_install_screen.box.install_instructions_part_1_ios_safari");
    }

    // Android
    return t("pwa_install_screen.box.install_instructions_part_1_android");
  };

  const getInstructionsIcon = (): JSX.Element => {
    if (areWeInsideAWebview) {
      if (isiOS) {
        return <Feather name="share" size={24} color="black" />;
      }
      // Android
      return <Entypo name="dots-three-vertical" size={24} color="black" />;
    }

    // Outside webview (in normal browser)
    if (isiOS) {
      return <MaterialIcons name="ios-share" size={24} color="black" />;
    }
    return <Entypo name="dots-three-vertical" size={24} color="black" />;
  };

  const getInstructionsPart2 = (): string => {
    if (areWeInsideAWebview) {
      if (isiOS) {
        return t("pwa_install_screen.box.instagram_instructions_part_2_ios");
      }
      // Android
      return t("pwa_install_screen.box.instagram_instructions_part_2_android_webview");
    }

    // Outside webview (in normal browser)
    if (isiOS) {
      if (platform.chrome) {
        return t("pwa_install_screen.box.install_instructions_part_2_ios_chrome");
      }
      return t("pwa_install_screen.box.install_instructions_part_2_ios_safari");
    }

    return t("pwa_install_screen.box.install_instructions_part_2_android");
  };

  return (
    <Box alignItems="center">
      <Box
        maxW="80"
        rounded="lg"
        overflow="hidden"
        borderColor="coolGray.200"
        borderWidth="1"
        _dark={{
          borderColor: "coolGray.600",
          backgroundColor: "gray.700",
        }}
        _web={{
          shadow: 2,
          borderWidth: 0,
        }}
        _light={{
          backgroundColor: "gray.50",
        }}
      >
        <Stack p="4" space={3}>
          <Stack space={2}>
            <Heading size="md" ml="-1" alignSelf="center">
              {t("pwa_install_screen.box.heading", { appName })}
            </Heading>
          </Stack>

          <NBView mt="4">
            <Row mt="8" alignSelf="center">
              {/* Tap on */}
              <Text fontSize="md" mr="2">
                {getInstructionsText()}
              </Text>
              {/* [SHARE ICON] */}
              {getInstructionsIcon()}
              {/* "then 'Open in external browser'" */}
            </Row>
            <Row mt="2" alignSelf="center">
              <Text fontSize="md">{getInstructionsPart2()}</Text>
            </Row>
          </NBView>
        </Stack>
      </Box>
    </Box>
  );
};

// New component for the iOS install banner (tooltip style)
const IOSInstallBanner = (): JSX.Element => {
  const { t } = useTranslation();

  const shareIcon = <MaterialIcons name="ios-share" size={20} color="black" />; // Standard iOS blue, adjusted size
  const addIcon = <Octicons name="diff-added" size={20} color="black" />;

  return (
    // This outer Box positions the trigger for the Popover
    <Box position="absolute" bottom="5px" alignSelf="center" zIndex={10} pointerEvents="none">
      <Popover
        isOpen={true} // Banner is always open when this component is rendered
        placement="top" // Content appears above the trigger, arrow points down from content
        trigger={(triggerProps) => (
          // Invisible trigger, Popover content will be positioned relative to this
          <Box {...triggerProps} h={1} w={1} />
        )}
        _backdrop={{ bg: "transparent", pointerEvents: "none" }}
      >
        <Popover.Content
          bg="coolGray.100"
          shadow={2}
          borderRadius="md"
          w="auto"
          maxW="95%"
          alignSelf="center"
          pointerEvents="auto"
        >
          <Popover.Arrow />
          <Popover.Body paddingY={6}>
            <Row alignItems="center" space={2}>
              {addIcon}
              <Text fontSize="sm" flexShrink={1} color="coolGray.800" bg="transparent">
                {t("pwa_install_screen.ios_install_banner.text_before_share_icon")}
                {shareIcon}
                {t("pwa_install_screen.ios_install_banner.text_after_share_icon_before_bold")}
                <Text bold>{t("pwa_install_screen.ios_install_banner.bold_text_add_to_home_screen")}</Text>
              </Text>
            </Row>
          </Popover.Body>
        </Popover.Content>
      </Popover>
    </Box>
  );
};

const PWASignupScreen = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [deferredPrompt, setDeferredPrompt] = React.useState<BeforeInstallPromptEvent | null>(null);

  const { appinstalled, canInstallprompt, enabledA2hs, enabledPwa, isLoading, isPwa, showInstallPrompt, userChoice } =
    usePwa();

  console.trace("PWASignupScreen: Initial usePwa state", {
    appinstalled,
    canInstallprompt,
    enabledA2hs,
    enabledPwa,
    isLoading,
    isPwa,
    userChoice,
  });

  React.useEffect(() => {
    console.log("PWASignupScreen: useEffect for beforeinstallprompt setup");
    const handler = (e: BeforeInstallPromptEvent): void => {
      console.trace("PWASignupScreen: beforeinstallprompt event handler started", e);
      // Prevent the browser's default install prompt.
      e.preventDefault();
      console.trace("PWASignupScreen: Browser's default prompt prevented.");

      // Stash the event. It might still be useful for detecting installability,
      // but calling e.prompt() later may not work if the browser already prompted.
      console.trace("PWASignupScreen: Stashing the event.");
      setDeferredPrompt(e);
      console.trace("PWASignupScreen: beforeinstallprompt event captured. Browser may show its own install UI.");
    };

    window.addEventListener("beforeinstallprompt", handler);

    return () => {
      console.trace("PWASignupScreen: useEffect for beforeinstallprompt cleanup");
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  React.useEffect(() => {
    console.trace("PWASignupScreen: deferredPrompt state changed", deferredPrompt);
  }, [deferredPrompt]);

  // Determine conditions for showing the new iOS install banner
  const { userAgent } = navigator; // Ensure navigator is available or handle SSR if needed
  const areWeInsideAWebview = typeof window !== "undefined" ? isWebview(userAgent) : false;
  const isiOS = typeof window !== "undefined" ? platform.ios : false;
  const isSafari = typeof window !== "undefined" ? platform.safari : false;
  const isPWA = typeof window !== "undefined" ? platform.pwa : false;
  const showIOSInstallBanner = isiOS && isSafari && !areWeInsideAWebview && !isPWA;

  const imageSize = Scale(ScreenWidth * 0.8 * 0.5);

  function onSubmitDeferPwaInstallPrompt(): void {
    dispatch(userSlice.actions.deferPwaInstallPrompt());
  }

  // TODO: This needs to be checked
  const shouldShowInstructionsBox = !showIOSInstallBanner && !deferredPrompt && canInstallprompt;

  return (
    <View style={styles.wrapper}>
      <View style={{ ...styles.loginContainer, marginTop: -200 }}></View>
      <View style={styles.container}>
        <KeyboardAvoidingView behavior="position">
          <Center>
            <AppImage />

            {/* Conditionally render InstallInstructionsBox */}
            {shouldShowInstructionsBox ? (
              <Box>
                <InstallInstructionsBox />
              </Box>
            ) : null}

            {/* Button to manually trigger the PWA install prompt */}
            {deferredPrompt && canInstallprompt ? (
              <Button
                onPress={() => {
                  if (deferredPrompt) {
                    console.trace("PWASignupScreen: Manual install button clicked. Attempting to call prompt().");
                    deferredPrompt.prompt().catch((err) => {
                      // Handle or log potential errors from the prompt() call itself, if necessary.
                      console.error("Error during prompt():", err);
                    });
                    // Wait for the user to respond to the prompt
                    deferredPrompt.userChoice
                      .then((choiceResult) => {
                        console.trace("PWASignupScreen: userChoice after manual prompt:", choiceResult);
                        if (choiceResult.outcome === "accepted") {
                          console.trace("PWASignupScreen: User accepted the A2HS prompt (manual)");
                        } else {
                          console.trace("PWASignupScreen: User dismissed the A2HS prompt (manual)");
                        }
                        // Clear the deferredPrompt so the button disappears after interaction.
                        setDeferredPrompt(null);
                      })
                      .catch((error: Error) => {
                        console.error("PWASignupScreen: Error in userChoice.then (manual prompt)", error);
                        setDeferredPrompt(null); // Also clear on error
                      });
                  }
                }}
                mt="4"
                size="lg"
                bg="green.600"
                rounded="lg"
                px="12"
                py="4"
              >
                <Text color="white" fontSize="lg" bold>
                  Install App
                </Text>
              </Button>
            ) : null}

            <NBView marginTop="6">
              <TroubleInstallingPWAArticleComponent />
            </NBView>

            <PresenceTransition
              visible={true}
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
                transition: {
                  duration: 250,
                  delay: 5000,
                },
              }}
            >
              <Button onPress={onSubmitDeferPwaInstallPrompt} mt="8" size="sm" variant="outline">
                <Text>{t("pwa_install_screen.defer_installation_button_text")}</Text>
              </Button>
            </PresenceTransition>
          </Center>
        </KeyboardAvoidingView>
      </View>
      {showIOSInstallBanner ? <IOSInstallBanner /> : null}
    </View>
  );
};

export default PWASignupScreen;
